import { useDispatchDeleteActiveObjectEvent } from "@/components/canvas/hooks/event/useDeleteActiveObjectEvent";
import { useDispatchChangeImageOptionsEvent } from "@/components/canvas/hooks/event/useImageChangeOptionsEvent";
import { useDispatchSetAsBackgroundImageEvent } from "@/components/canvas/hooks/event/useSetBackgroundImageEvent";
import { selectImageOptions, selectIsSizeRatioLocked } from "@/store/canvas/canvasSelectors";
import { useDispatch, useSelector } from "react-redux";
import { ImageInspectorContent } from "./ImageInspectorContent";
import { setImageOptions } from "@/store/canvas/canvasSlice";
import { ModifyImageSettings } from "@/components/canvas/types/imageTypes";
import { useDispatchChangeLayerEvent } from "@/components/canvas/hooks/event/useChangeLayerEvent";

export const ImageInspector = () => {
  const dispatch = useDispatch();
  const imageOptions = useSelector(selectImageOptions);
  const isSizeRatioLocked = useSelector(selectIsSizeRatioLocked);
  const dispatchChangeLayer = useDispatchChangeLayerEvent();
  const dispatchChangeImageOptions = useDispatchChangeImageOptionsEvent();
  const dispatchDeleteActiveObject = useDispatchDeleteActiveObjectEvent();
  const dispatchSetAsBackgroundImage = useDispatchSetAsBackgroundImageEvent();

  const createOptionChangeHandler =
    <T extends keyof NonNullable<typeof imageOptions>>(key: T) =>
    (value: NonNullable<typeof imageOptions>[T]) => {
      if (!imageOptions) return;

      const updatedOptions = { ...imageOptions, [key]: value };
      dispatch(setImageOptions(updatedOptions));

      const imageSettings: ModifyImageSettings = { modifiedPropertyName: key, isSizeRatioLocked };
      dispatchChangeImageOptions({ ...updatedOptions, ...imageSettings });
    };

  if (!imageOptions) {
    return null;
  }

  return (
    <ImageInspectorContent
      rotationOption={{ value: imageOptions.angle, onChange: createOptionChangeHandler("angle") }}
      widthOption={{ value: imageOptions.width, onChange: createOptionChangeHandler("width") }}
      heightOption={{ value: imageOptions.height, onChange: createOptionChangeHandler("height") }}
      xPositionOption={{
        value: imageOptions.left,
        onChange: createOptionChangeHandler("left"),
      }}
      yPositionOption={{
        value: imageOptions.top,
        onChange: createOptionChangeHandler("top"),
      }}
      strokeColorOption={{
        value: imageOptions.stroke ?? "#ffffff",
        onChange: createOptionChangeHandler("stroke"),
      }}
      strokeWidthOption={{
        value: imageOptions.strokeWidth,
        onChange: createOptionChangeHandler("strokeWidth"),
      }}
      opacityOption={{
        value: imageOptions.opacity,
        onChange: createOptionChangeHandler("opacity"),
      }}
      onLayerChange={dispatchChangeLayer}
      onDeleteElementClick={() => dispatchDeleteActiveObject()}
      onSetAsBackgroundImageClick={() => dispatchSetAsBackgroundImage()}
    />
  );
};
